<template>
  <BaseTable
    :headers="headers"
    :rows="rows"
    @action="onAction"
    v-test="'messages'"
  ></BaseTable>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import type { Message } from '@/types';
import { RESEND_MESSAGE } from './graphql';

const { t } = useI18n();

const props = defineProps<{
  messages: Message[];
  filter: string;
}>();

const destinationInfo = (message: Message) => {
  if (message.gateway === 'email' && message.destination) {
    return message.destination;
  } else if (
    message.gateway === 'sms' &&
    message.customer &&
    message.customer.mobilePhone
  ) {
    return message.customer.mobilePhone;
  }
  return null;
};

const subjectInfo = (message: Message) => {
  if (message.subject) {
    return message.subject;
  } else {
    return {
      html: `${message.message?.substring(0, 50)}...`,
    };
  }
};

const statusColor = (status: string) => {
  return status === 'sent' || status === 'delivered'
    ? 'success'
    : status !== 'sent' && status !== 'delivered' && status !== 'pending'
      ? 'warning'
      : 'default';
};

const hasTrackingData = computed(() => {
  return (
    props.filter === 'rebook_reminder' || props.filter === 'marketing_campaign'
  );
});

const headers = computed(() => {
  const headersArr = [
    // space for the email/mobile icon
    '',
    t('global.sent'),
    t('global.type'),
    t('global.items.customer', 1),
    t('global.contact'),
    t('global.message'),
    t('global.status'),
  ];

  if (hasTrackingData.value) {
    // space for the tracking icons
    headersArr.push('', '');
  }

  return headersArr;
});

const cells = (message: Message) => {
  const cellsArr = [
    {
      icon: message.gateway === 'email' ? 'email' : 'mobile',
    },
    {
      value: filters.dateTime(message.createdAt),
      type: 'date',
    },
    t(`messages.types.${message.messageType}`),
    message.customer?.fullName,
    destinationInfo(message),
    subjectInfo(message),
    message.status
      ? {
          label: {
            text: `${t(`messages.status.${message.status}`)}`,
            color: statusColor(message.status),
          },
          ...(message.customerArchived
            ? { icon: 'info', tooltip: t('messages.customer_archived') }
            : {}),
        }
      : null,
  ];

  if (hasTrackingData.value) {
    cellsArr.push(
      {
        icon: 'email-open',
        tooltip: message.openedAt
          ? t('messages.opened_at', {
              time: filters.dateTime(message.openedAt),
            })
          : null,
        disabled: !message.openedAt,
      },
      {
        icon: 'button-clicked',
        tooltip: message.buttonClickAt
          ? t('messages.button_click_at', {
              time: filters.dateTime(message.buttonClickAt),
            })
          : null,
        disabled: !message.buttonClickAt,
      },
    );
  }

  return cellsArr;
};

const actions = (message: Message) => {
  if (
    message.customerArchived ||
    message.gateway === 'sms' ||
    message.messageType === 'marketing_campaign' ||
    message.messageType === 'rebook_reminder' ||
    message.messageType === 'custom' ||
    message.messageType === 'feedback_response' ||
    message.status === 'pending' ||
    message.messageType === 'form_submission_request' ||
    message.status === 'undeliverable'
  ) {
    return [];
  }
  return [
    {
      name: 'resend',
      icon: 'arrow-corner-up-right',
      label: t('global.actions.resend'),
    },
  ];
};

const rows = computed(() => {
  return props.messages.map((message) => ({
    id: message.id,
    routerLink: message.customerArchived
      ? undefined
      : {
          name: 'message',
          query: props.filter ? { filter: props.filter } : {},
          params: {
            messageId: message.id,
          },
        },
    cells: cells(message),
    actions: actions(message),
  }));
});

const onAction = ({ id, option }: { id: number; option: string }) => {
  if (option === 'resend') {
    const input = { messageId: id };

    const { mutate } = useMutation(RESEND_MESSAGE, {
      variables: { input },
    });

    modal('confirmation').then(() => {
      mutate().then(() => {
        flash(t('global.flash.msg_on_its_way'));
      });
    });
  }
};
</script>
